import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { listAllUsers, listLocations, exportDirectory } from "../api";
import AppPage from "../components/AppPage";

const cell = (value) => {
  const v = (value || "").toString().trim();
  return v || "—";
};

export default function Directory() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'pdf' | null
  const [exportErr, setExportErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [usersData, locsData] = await Promise.all([
          listAllUsers(),
          listLocations().catch(() => []),
        ]);
        setUsers(usersData || []);
        setLocations(locsData || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const locationNameById = useMemo(() => {
    const map = {};
    for (const loc of locations) {
      if (loc?.location_id) map[loc.location_id] = loc.name || loc.short_code || "";
    }
    return map;
  }, [locations]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return users
      .map((u) => ({
        ...u,
        location_name: locationNameById[u.location_id] || "",
      }))
      .filter((u) => {
        if (!s) return true;
        return (
          (u.name || "").toLowerCase().includes(s) ||
          (u.email || "").toLowerCase().includes(s) ||
          (u.position || "").toLowerCase().includes(s) ||
          (u.phone || "").toLowerCase().includes(s) ||
          (u.location_name || "").toLowerCase().includes(s)
        );
      })
      .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || "", "ru"));
  }, [users, search, locationNameById]);

  const onExport = async (format) => {
    setExportErr("");
    setExporting(format);
    try {
      await exportDirectory(format);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setExportErr(typeof detail === "string" ? detail : (e.message || t("common.error")));
    } finally {
      setExporting(null);
    }
  };

  return (
    <AppPage
      testId="directory-page"
      bodyScroll="table"
      toolbar={(
        <>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div>
              <h4 className="fw-bold mb-0">
                <i className="bi bi-journal-text me-2"></i>
                {t("directory.title")}
              </h4>
              {company?.name && (
                <div className="text-muted small mt-1">
                  <i className="bi bi-building me-1"></i>{company.name}
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                disabled={!!exporting || loading || !user?.company_id}
                onClick={() => onExport("xlsx")}
                data-testid="directory-export-xlsx"
                title={t("directory.export_xlsx_title")}
              >
                {exporting === "xlsx" ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="bi bi-file-earmark-excel me-1"></i>
                )}
                {t("directory.export_xlsx")}
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                disabled={!!exporting || loading || !user?.company_id}
                onClick={() => onExport("pdf")}
                data-testid="directory-export-pdf"
                title={t("directory.export_pdf_title")}
              >
                {exporting === "pdf" ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                )}
                {t("directory.export_pdf")}
              </button>
              <div className="text-muted small" data-testid="directory-counter">
                {t("directory.found")}: <strong>{filtered.length}</strong>
              </div>
            </div>
          </div>
          {exportErr && <div className="alert alert-danger py-2">{exportErr}</div>}
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <input
              className="form-control flex-grow-1"
              style={{ maxWidth: 480 }}
              placeholder={t("directory.search_within_company_ph")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="directory-search"
            />
          </div>
        </>
      )}
    >
      {loading ? (
        <div className="text-muted text-center py-5">
          <div className="spinner-border spinner-border-sm me-2"></div>
          {t("common.loading")}
        </div>
      ) : !user?.company_id ? (
        <div className="text-muted text-center py-5">
          <i className="bi bi-building-x fs-1 d-block mb-2"></i>
          {t("directory.no_company_yet")}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-muted text-center py-5">
          <i className="bi bi-search fs-1 d-block mb-2"></i>
          {t("directory.no_results")}
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle" data-testid="directory-table">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                <tr>
                  <th>{t("directory.col_name")}</th>
                  <th>{t("directory.col_position")}</th>
                  <th>{t("directory.col_phone")}</th>
                  <th>{t("directory.col_email")}</th>
                  <th>{t("directory.col_location")}</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.user_id} data-testid={`directory-row-${u.user_id}`}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {u.photo_b64 || u.picture ? (
                          <img
                            src={u.photo_b64 || u.picture}
                            alt=""
                            className="rounded-circle flex-shrink-0"
                            width="32"
                            height="32"
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 32, height: 32, background: "#e9ecf2" }}
                          >
                            <i className="bi bi-person text-muted"></i>
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="fw-semibold text-truncate">{cell(u.name || u.email)}</div>
                          {u.user_id === user?.user_id && (
                            <span className="badge bg-light text-dark border">{t("directory.you")}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="small">{cell(u.position)}</td>
                    <td className="small">
                      {u.phone ? (
                        <a href={`tel:${u.phone}`} className="text-decoration-none">{u.phone}</a>
                      ) : "—"}
                    </td>
                    <td className="small">
                      {u.email ? (
                        <a href={`mailto:${u.email}`} className="text-decoration-none text-truncate d-inline-block" style={{ maxWidth: 220 }}>
                          {u.email}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="small">{cell(u.location_name)}</td>
                    <td>
                      {u.user_id !== user?.user_id && (
                        <Link
                          to={`/chat/${u.user_id}`}
                          className="btn btn-sm btn-outline-primary"
                          data-testid={`chat-btn-${u.user_id}`}
                        >
                          <i className="bi bi-chat-dots me-1"></i>{t("directory.write")}
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppPage>
  );
}
